// The single source of the workbench navigation (portal workbench spec,
// docs/superpowers/specs/2026-09-29-portal-workbench-design.md): groups,
// items, routes, who sees each item, and where a user lands. A pure function
// of (claims, scope), with no JSX.
//
// Display only. Hiding an item is UX: every route still hits the same
// backend checks (portal/backend/lib/auth.mjs), which stay the only
// enforcement point. The active item comes from the route path alone, so two
// items can never be current at once (the ENTRAID-267 class of bug).

import { effectiveAccess } from './tenant-access.js';

/**
 * @typedef {{ isOperator: boolean, customerId: string|null, admin: boolean, developer: boolean, qlikCredentials: boolean, auditor: boolean, viewer: boolean, reviewer: boolean }} NavClaims
 * @typedef {{ kind: 'all' } | { kind: 'customer', customerId: string }} Scope
 * @typedef {{ id: string, label: string, to: string, keywords?: string[] }} NavItem
 * @typedef {{ id: string, label: string, items: NavItem[] }} NavGroup
 */

/** The operators' scope: every customer. */
export const ALL_CUSTOMERS = Object.freeze({ kind: 'all' });

/** One customer's scope. */
export function customerScope(customerId) {
  return Object.freeze({ kind: 'customer', customerId });
}

export const validId = (id) => typeof id === 'string' && id.length > 0;
const ownsScope = (c, scope) => scope.kind === 'customer' && validId(c.customerId) && c.customerId === scope.customerId;
const operatorOrAdmin = (c, scope) => c.isOperator === true || (ownsScope(c, scope) && c.admin === true);
const operatorAdminOr = (role) => (c, scope) => operatorOrAdmin(c, scope) || (ownsScope(c, scope) && c[role] === true);
// The Access Reviews reviewer role alone (spec 2026-10-29): "Your reviews" is all it opens, so Access reviews goes
// straight there (access-reviews/mine). With the admin, the operator or the viewer role the page itself is the entry.
const reviewerOnly = (c, scope) => ownsScope(c, scope) && c.reviewer === true && !operatorAdminOr('viewer')(c, scope);

/**
 * True when this user may load a customer's record (GET /customers/{id}):
 * operators, and that customer's own admin. A developer or a qlik-credentials
 * holder never can; they get their sections only.
 */
export function readsCustomerRecord(claims, customerId) {
  return validId(customerId) && operatorOrAdmin(claims ?? {}, customerScope(customerId));
}

/**
 * True when this user may load the record Overview and Security are built on
 * (GET /customers/{id}) and their score trend (GET .../runs, summary view):
 * everyone readsCustomerRecord admits, plus that customer's viewer role, which
 * the API answers with a narrowed record.
 */
export function readsOverviewRecord(claims, customerId) {
  return validId(customerId) && operatorAdminOr('viewer')(claims ?? {}, customerScope(customerId));
}

/**
 * True when this user reaches the customer only as its read-only viewer (not an
 * operator, not its admin): Overview and Security render with no action and no
 * link to a page it cannot open. Display only; the API enforces it.
 */
export function viewerOnly(claims, customerId) {
  const c = claims ?? {};
  return validId(customerId) && !operatorOrAdmin(c, customerScope(customerId)) && ownsScope(c, customerScope(customerId)) && c.viewer === true;
}

// Sidebar order (ENTRAID-347 redesign, ia-proposal section 2, the A5 menu): Monitor answers, Configure changes,
// Implementation shows what runs and how to build on it. Sentence case labels. Roles combine: the sidebar shows the union. `keywords` are the old names and the
// parts a page holds, so Quick find still finds a page by what it used to be called (display only, never a route).
const CUSTOMER_GROUPS = Object.freeze([
  {
    id: 'monitor',
    label: 'Monitor',
    items: [
      // The viewer role (2026-10-25) sees these and nothing else.
      { id: 'overview', label: 'Overview', visible: operatorAdminOr('viewer'), keywords: ['home', 'score', 'changes since yesterday'] },
      { id: 'security', label: 'Security', visible: operatorAdminOr('viewer'), keywords: ['checks', 'findings', 'secure score', 'posture'] },
      // License Insights (Hugo Andrade, 2026-10-26): every tier; the viewer reads aggregates only (no names, no editing).
      { id: 'licenses', label: 'Licenses', visible: operatorAdminOr('viewer'), keywords: ['license insights', 'cost', 'savings', 'waste', 'seats', 'subscriptions', 'SKU', 'products'] },
      // Access reviews (spec 2026-10-26, section 5): campaigns on professional and up, findings on every tier; the viewer
      // and the operator read counts only (no names, no Setup, no actions). Display only: the API decides.
      { id: 'access-reviews', label: 'Access reviews', visible: (c, scope) => operatorAdminOr('viewer')(c, scope) || reviewerOnly(c, scope), sub: (c, scope) => (reviewerOnly(c, scope) ? 'mine' : null), keywords: ['access review', 'recertification', 'certification', 'attestation', 'privileged roles', 'admin roles', 'least privilege', 'quarterly review', 'campaign'] },
      // The Watch events and flagged cases, moved out of Notifications (plan C3). The same access Notifications had; an
      // operator account sees Watch health only (the portal refuses it Watch content).
      { id: 'activity', label: 'Activity', visible: operatorOrAdmin, keywords: ['watch', 'events', 'flagged', 'cases'] },
      // ENTRAID-341: Reports hub (was Compliance). Operators see the health-only view; an auditor sees nothing else.
      { id: 'reports', label: 'Reports', visible: operatorAdminOr('auditor'), keywords: ['compliance', 'controls', 'attestations', 'evidence'] },
    ],
  },
  {
    id: 'configure',
    label: 'Configure',
    items: [
      // Sources, Collections and the schedule on one page (plan C5).
      { id: 'data-collection', label: 'Data collection', visible: operatorOrAdmin, keywords: ['sources', 'collections', 'collect now', 'run now', 'schedule', 'retention'] },
      // Its first tab is in the path (/notifications/rules): /notifications itself is the old Watch feed, now Activity.
      { id: 'notifications', label: 'Notifications', visible: operatorOrAdmin, sub: 'rules', keywords: ['rules', 'recipients', 'delivery', 'sending domain', 'teams', 'send log', 'alerts'] },
      { id: 'qlik-cloud', label: 'Qlik Cloud', visible: operatorAdminOr('qlikCredentials'), keywords: ['qlik'] },
      { id: 'people', label: 'People', visible: operatorOrAdmin, keywords: ['roles', 'access requests', 'guardians'] },
      // Company Settings and Deployment on one page, with the same access (plan C6).
      { id: 'settings', label: 'Settings', visible: operatorOrAdmin, keywords: ['company settings', 'branding', 'deployment', 'consent', 'microsoft connection', 'danger zone'] },
    ],
  },
  {
    id: 'implementation',
    label: 'Implementation',
    items: [
      // Implementation footprint (spec 2026-10-29): what onGuard runs in this tenant and AWS account. The developer and the
      // auditor read it too (the auditor gets this and Reports, never the Data reference). Display only: the API decides.
      // 'develop' keeps the old group name findable in Quick find.
      { id: 'implementation-footprint', label: 'Implementation footprint', visible: (c, s) => operatorAdminOr('developer')(c, s) || operatorAdminOr('auditor')(c, s), keywords: ['develop', 'footprint', 'what runs', 'schedule', 's3', 'paths', 'evidence'] },
      { id: 'data-reference', label: 'Data reference', visible: operatorAdminOr('developer'), keywords: ['fields', 'schema', 'guide'] },
    ],
  },
]);

// Operations: operators only, at the top of the sidebar in every scope (a customer selected or All customers), so the
// Customers page ("Add customer") and the Fleet overview are always one click away. Hidden in "View as client" (toNavClaims
// clears isOperator there). Quick find reads the keywords.
const OPERATIONS_GROUP = Object.freeze({
  id: 'operations',
  label: 'Operations',
  items: [
    { id: 'fleet-overview', label: 'Fleet overview', to: '/fleet', keywords: ['fleet', 'health', 'all customers', 'overview'] },
    { id: 'customers', label: 'Customers', to: '/customers', keywords: ['add customer', 'new customer', 'create customer', 'onboard', 'customer list'] },
  ],
});

// The All customers scope: operators only, below Operations.
const FLEET_GROUPS = Object.freeze([
  {
    id: 'implementation',
    label: 'Implementation',
    items: [
      { id: 'data-reference', label: 'Data reference', to: '/data-reference' },
    ],
  },
]);

const operationsGroup = () => ({
  id: OPERATIONS_GROUP.id,
  label: OPERATIONS_GROUP.label,
  items: OPERATIONS_GROUP.items.map(({ id, label, to, keywords }) => ({ id, label, to, keywords: [...keywords] })),
});

/** Every customer section id, in sidebar order; also the route segment. */
export const CUSTOMER_SECTIONS = Object.freeze(CUSTOMER_GROUPS.flatMap((g) => g.items.map((i) => i.id)));

const SECTION_LABELS = new Map(CUSTOMER_GROUPS.flatMap((g) => g.items.map((i) => [i.id, i.label])));

/** The sidebar label of a customer section, which is also its page title (ENTRAID-347 PageHeader). */
export function sectionLabel(sectionId) {
  return SECTION_LABELS.get(sectionId) ?? sectionId;
}

const FLEET_PATHS = new Map([...OPERATIONS_GROUP.items, ...FLEET_GROUPS.flatMap((g) => g.items)].map((i) => [i.to, i.id]));

// The sections of the destination scope itself: Operations is the same everywhere, so it never counts as "the same section".
const scopeItems = (nav) => nav.groups.filter((g) => g.id !== OPERATIONS_GROUP.id).flatMap((g) => g.items);

/** The route of one customer section. */
export function customerSectionPath(customerId, sectionId) {
  return `/customers/${encodeURIComponent(customerId)}/${sectionId}`;
}

// A sidebar item's link: the section, or its first tab when that tab is addressed by the path.
const itemPath = (customerId, item, c = {}, scope = null) => {
  const sub = typeof item.sub === 'function' ? item.sub(c, scope) : item.sub;
  return `${customerSectionPath(customerId, item.id)}${sub ? `/${sub}` : ''}`;
};

/** True when these claims reach this customer's Access reviews only through "Your reviews" (the reviewer role alone). */
export function reviewsOnly(claims, customerId) {
  return validId(customerId) && reviewerOnly(claims ?? {}, customerScope(customerId));
}

/**
 * The claims the UI already derives from GET /me (lib/tenant-access.js),
 * with an operator's "View as client" applied: while viewing as a client the
 * operator is that customer's admin, not an operator.
 *
 * @param {object|null} me - the GET /me body
 * @param {{ customerId: string, companyName?: string }|null} impersonating
 * @param {string|null} [scopeCustomerId] - the customer of the current page: a user of several customers gets the
 *   roles of that one (ENTRAID-307)
 * @returns {NavClaims}
 */
export function toNavClaims(me, impersonating, scopeCustomerId = null) {
  const access = effectiveAccess(me, impersonating, scopeCustomerId);
  return Object.freeze({
    isOperator: !impersonating?.customerId && me?.user?.isOperator === true,
    customerId: access.customerId,
    admin: access.admin === true,
    developer: access.developer === true,
    qlikCredentials: access.qlikCredentials === true,
    auditor: access.auditor === true,
    viewer: access.viewer === true,
    reviewer: access.reviewer === true,
  });
}

/**
 * The sidebar for these claims in this scope. Empty groups are hidden. Operators
 * also get Operations first, in every scope. The landing is the first visible
 * customer section (or /fleet in All customers).
 *
 * @param {NavClaims|null} claims
 * @param {Scope|null} scope
 * @returns {{ groups: NavGroup[], landing: string|null }}
 */
export function navigationFor(claims, scope) {
  const c = claims ?? {};
  const operator = c.isOperator === true;
  let groups = [];
  let landing = null;
  if (scope?.kind === 'all' && operator) {
    groups = [
      operationsGroup(),
      ...FLEET_GROUPS.map((g) => ({ id: g.id, label: g.label, items: g.items.map(({ id, label, to }) => ({ id, label, to })) })),
    ];
    landing = groups[0].items[0].to;
  } else if (scope?.kind === 'customer' && validId(scope.customerId)) {
    const customerGroups = CUSTOMER_GROUPS
      .map((g) => ({
        id: g.id,
        label: g.label,
        items: g.items
          .filter((i) => i.visible(c, scope))
          .map((i) => ({ id: i.id, label: i.label, to: itemPath(scope.customerId, i, c, scope), keywords: i.keywords ?? [] })),
      }))
      .filter((g) => g.items.length > 0);
    // The landing stays the first customer section: Operations is a way around, not where a customer scope starts.
    landing = customerGroups[0]?.items[0]?.to ?? null;
    groups = operator ? [operationsGroup(), ...customerGroups] : customerGroups;
  }
  return { groups, landing };
}

/** True when this item is in the sidebar for these claims in this scope. */
export function canSee(claims, scope, itemId) {
  return navigationFor(claims, scope).groups.some((g) => g.items.some((i) => i.id === itemId));
}

/** An operator starts in All customers; a customer user in their own tenant. */
export function homeScope(claims) {
  if (claims?.isOperator === true) return ALL_CUSTOMERS;
  if (validId(claims?.customerId)) return customerScope(claims.customerId);
  return null;
}

/** Where "/" sends this user: the first page they can use, or null (no access). */
export function landingRoute(claims) {
  const scope = homeScope(claims);
  return scope ? navigationFor(claims, scope).landing : null;
}

/**
 * The route of a customer section for this user: the section itself when
 * they can see it, else the first item they can see for that customer, else
 * their own landing, else "/" (which renders the no-access page).
 */
export function sectionTarget(claims, customerId, sectionId) {
  if (validId(customerId)) {
    const nav = navigationFor(claims, customerScope(customerId));
    const item = scopeItems(nav).find((i) => i.id === sectionId);
    if (item) return item.to;
    if (nav.landing) return nav.landing;
  }
  return landingRoute(claims) ?? '/';
}

/**
 * The scope and the active item of a path, from the path alone (never a
 * query string). Pages outside the sidebar have no scope.
 *
 * @returns {{ scope: Scope|null, itemId: string|null }}
 */
export function locate(pathname) {
  const path = String(pathname ?? '').replace(/\/+$/, '') || '/';
  if (FLEET_PATHS.has(path)) return { scope: ALL_CUSTOMERS, itemId: FLEET_PATHS.get(path) };
  const match = /^\/customers\/([^/]+)(?:\/([^/]+))?(?:\/.*)?$/.exec(path);
  if (!match) return { scope: null, itemId: null };
  let customerId;
  try {
    customerId = decodeURIComponent(match[1]);
  } catch {
    return { scope: null, itemId: null };
  }
  const itemId = match[2] && CUSTOMER_SECTIONS.includes(match[2]) ? match[2] : null;
  return { scope: customerScope(customerId), itemId };
}

/**
 * Where the customer switcher goes: the current section in the target scope
 * when the target shows it, otherwise the target's landing rule.
 */
export function switchScopeTarget(claims, pathname, target) {
  const { itemId } = locate(pathname);
  const nav = navigationFor(claims, target);
  const kept = itemId ? scopeItems(nav).find((i) => i.id === itemId) : null;
  return kept?.to ?? nav.landing ?? '/';
}
